import { Link } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export const ExamReport = ({ exam, unitIndex }) => {
  const r = exam.report;
  const pass = r.est_score >= 145;
  return (
    <div data-testid="exam-report">
      <section className={`rounded-[2rem] p-8 ${pass ? "bg-[#E3EBE5]" : "bg-[#FBEDE8]"}`}>
        <p className="text-xs uppercase tracking-widest text-stone-500">Estimated GED score</p>
        <div className="flex flex-wrap items-end gap-6 mt-2">
          <span className="font-display text-7xl text-[#2C4A3E]" data-testid="exam-est-score">{r.est_score}</span>
          <div className="pb-2"><p className="font-display text-2xl text-[#2C4A3E]" data-testid="exam-band">{r.band}</p>
            <p className="text-sm text-stone-600">{r.correct}/{r.total} correct{r.essay_score != null && ` · essay ${r.essay_score}/6`} · {r.minutes_used} of {exam.duration_min} min{r.timed_out && " · time ran out"}</p></div>
        </div>
        <div className="relative h-3 rounded-full bg-white/70 mt-6">
          <div className="absolute inset-y-0 left-0 rounded-full bg-[#6B9080]" style={{ width: `${r.est_score - 100}%` }} />
          <div className="absolute -top-1 h-5 w-0.5 bg-[#2C4A3E]" style={{ left: "45%" }} title="145 pass" />
          <div className="absolute -top-1 h-5 w-0.5 bg-[#2C4A3E]/50" style={{ left: "65%" }} title="165 college ready" />
        </div>
        <div className="relative h-4 text-[11px] text-stone-500 mt-1">
          <span className="absolute left-0">100</span><span className="absolute -translate-x-1/2" style={{ left: "45%" }}>145 pass</span>
          <span className="absolute -translate-x-1/2" style={{ left: "65%" }}>165 college ready</span><span className="absolute right-0">200</span></div>
        <p className="text-xs text-stone-500 mt-4" data-testid="exam-estimate-note">This is an estimate from a ConsciousLearn practice exam, not an official GED score. Official scores come only from the GED test at GED.com.</p>
      </section>
      <section className="grid md:grid-cols-2 gap-6 mt-8">
        <div className="rounded-3xl bg-white/70 border border-[#E6DEC3] p-6" data-testid="exam-by-unit">
          <p className="text-xs uppercase tracking-widest text-stone-500">Results by skill area</p>
          {r.by_unit.map((u) => (
            <div key={u.unit} className="mt-4"><div className="flex justify-between text-sm"><span>{u.unit}</span><b>{u.correct}/{u.total}</b></div>
              <div className="h-2 rounded-full bg-[#F1EADB] mt-1"><div className="h-2 rounded-full bg-[#6B9080]" style={{ width: `${(100 * u.correct) / u.total}%` }} /></div></div>
          ))}
        </div>
        <div className="rounded-3xl bg-white/70 border border-[#E6DEC3] p-6" data-testid="exam-study-next">
          <p className="text-xs uppercase tracking-widest text-stone-500">Study next</p>
          {r.study_next.length ? r.study_next.map((u) => (
            <Link key={u} to={`/pathway/lesson/ged/${exam.subject_id}/${unitIndex(u)}`} className="block mt-3 px-4 py-3 rounded-xl bg-[#F7F3EB] hover:bg-[#F1EADB] text-sm text-[#2C4A3E]">{u} →</Link>
          )) : <p className="mt-3 text-sm text-stone-600">Strong across every skill area. You may be ready to schedule the official test!</p>}
          {r.essay_feedback && <><p className="text-xs uppercase tracking-widest text-stone-500 mt-6">Essay feedback</p><p className="text-sm text-stone-700 mt-2" data-testid="exam-essay-feedback">{r.essay_feedback}</p></>}
        </div>
      </section>
      <h2 className="font-display text-2xl text-[#2C4A3E] mt-10">Review answers</h2>
      <Accordion type="multiple" className="mt-4" data-testid="exam-review">
        {exam.review.map((q, i) => (
          <AccordionItem key={i} value={`q${i}`} className="border-[#E6DEC3]">
            <AccordionTrigger className="text-left text-sm" data-testid={`exam-review-${i}`}>
              <span className="inline-flex items-center gap-2">{q.choice === q.answer_index ? <CheckCircle2 className="w-4 h-4 text-[#6B9080] shrink-0" /> : <XCircle className="w-4 h-4 text-[#C87D53] shrink-0" />}{i + 1}. {q.question.slice(0, 90)}{q.question.length > 90 && "…"}</span>
            </AccordionTrigger>
            <AccordionContent>
              <p className="text-sm whitespace-pre-wrap">{q.question}</p>
              <ul className="mt-2 text-sm space-y-1">{q.options.map((o, j) => <li key={j} className={j === q.answer_index ? "text-[#2C4A3E] font-semibold" : j === q.choice ? "text-[#C0392B] line-through" : "text-stone-600"}>{"ABCD"[j]}. {o}</li>)}</ul>
              <p className="text-sm text-stone-600 mt-2 italic">{q.explanation} ({q.unit})</p>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};
